import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowClickedEvent,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { Group, Panel } from "react-resizable-panels";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { CustomStatusPanel } from "@/controls/ag-grid/components/CustomStatusPanel";
import { Carrier } from "@/pages/catalogs/models/Carrier";
import { CatalogsActionsHeader } from "@/pages/catalogs/shared/components/CatalogsActionsHeader";
import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { MultipleDeleteModal } from "@/pages/catalogs/shared/components/MultipleDeleteModal";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { themeDXT } from "@/setup/ag-grid-license";
import { routeCarrier } from "@/setup/routes/catalog.routes";
import { DeleteEntity } from "@/shared/components/DeleteEntity";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { useNavigate } from "react-router-dom";
import { deleteCarrier } from "../../../repositories/CarrierRepository";
import { useCarriers } from "../CarrierPage";
import { useCarrierOptionsFunctions } from "../hooks/useCarrierOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { CarrierDetails } from "./CarrierDetails";

export const TableCarrier = ({ partId }: { partId: string | undefined }) => {
	const navigate = useNavigate();

	const [filterValue, setFilterValue] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<Carrier | null>(null);
	const [selectedRows, setSelectedRows] = useState<Carrier[]>([]);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | "deleteMultiple" | ""
	>("");
	const [error, setError] = useState("");

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("carrier-table-panel-sizes");

	const { data: rows, isLoading } = useCarriers(undefined, (error) =>
		setError(error.message),
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useCarrierOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowSelected = (event: RowSelectedEvent) => {
		const selectedNodes = gridApi?.getSelectedNodes() || [];
		const selectedData = selectedNodes.map((node) => node.data as Carrier);
		setSelectedRows(selectedData);
	};

	const onRowClicked = (event: RowClickedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.data) {
			setSelectedRowData(event.data);
			setShowModal("update");
			navigate(`${routeCarrier}/${(event.data as Carrier).PartId}`);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			const selectedRows = params.api?.getSelectedRows();
			showContextMenu(
				event as any,
				{ ...data, selectedRows: selectedRows },
				"carrierContext",
			);
			if (data && selectedRows.length <= 1) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			setTimeout(() => {
				navigate(`${routeCarrier}/${entityId}}`);
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode!) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	const handleClearSelection = () => {
		gridApi?.deselectAll();
		setSelectedRows([]);
	};

	const handleNewCarrier = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`${routeCarrier}/new`);
	};

	useEffect(() => {
		if (!isLoading) {
			if (partId) {
				if (partId === "new") {
					handleNewCarrier();
				} else {
					const partNode = gridApi?.getRowNode(partId);

					if (partNode) {
						gridApi?.ensureNodeVisible(partNode);

						partNode.setSelected(true);
						setSelectedRowData(partNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "carrierContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2
				container
				justifyContent="flex-start"
				direction="column"
				p={2}
				spacing={1}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Carriers
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => {
							setFilterValue(val);
							gridApi?.setGridOption("quickFilterText", val);
						}}
					/>
				</Grid2>
				<Grid2 sx={{ width: "100%" }}>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							{selectedRows.length > 0 && (
								<CatalogsActionsHeader
									selectedRows={selectedRows}
									isDeletingAll={selectedRows.length == rows?.length}
									handleClearSelection={handleClearSelection}
									handleDelete={() => {
										if (selectedRows.length > 1) {
											setShowModal("deleteMultiple");
										} else {
											setShowModal("delete");
										}
									}}
									isLoading={false}
								/>
							)}
							<Paper
								elevation={2}
								style={{ height: "100%", width: "100%" }}
								id="carrier-table"
								className="ag-theme-material"
							>
								<AgGridReact<Carrier>
									loading={isLoading}
									rowData={rows || []}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									rowHeight={34}
									headerHeight={34}
									animateRows
									getContextMenuItems={getContextMenuItems}
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) => String(params.data.PartId)}
									loadingOverlayComponent={CenteredLazyLoading}
									onRowSelected={onRowSelected}
									onRowClicked={onRowClicked}
									gridOptions={{
										theme: themeDXT,
										rowSelection: {
											mode: "multiRow",
											checkboxes: true,
											enableClickSelection: false,
											headerCheckbox: true,
											isRowSelectable: (node) =>
												node.data?.CarrierStatusCode == "E" ||
												node.data?.CarrierStatusCode == "X",
										},
										statusBar: {
											statusPanels: [
												{
													statusPanel: "agTotalRowCountComponent",
													align: "right",
												},
												{
													statusPanel: (params: IStatusPanelParams) => (
														<CustomStatusPanel
															handleNew={handleNewCarrier}
															id="new-carrier"
														/>
													),
													align: "left",
												},
											],
										},
									}}
								/>
							</Paper>
						</Panel>
						<CustomSeparator onDoubleClick={handleSeparatorDoubleClick} />
						<Panel id="details" minSize={20}>
							<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
								<CarrierDetails
									carrier={selectedRowData}
									onHide={onModalHide}
									modal={
										showModal === "deleteMultiple"
											? ""
											: (showModal as "create" | "update" | "delete" | "")
									}
								/>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<DeleteEntity
				show={showModal === "delete"}
				entityParams={{
					Id: selectedRowData?.PartId!,
					Name: selectedRowData?.PartName!,
					Entity: "carrier",
				}}
				onHide={(update: boolean) => {
					onModalHide({ shouldUpdate: update, close: true });
					setShowModal(update ? "" : "update");
				}}
				deleteFunction={deleteCarrier}
			/>
			<MultipleDeleteModal
				open={showModal === "deleteMultiple"}
				onClose={(deleted: boolean) => {
					if (deleted) {
						setSelectedRows([]);
					}
					setShowModal("");
				}}
				deleteFunction={deleteCarrier}
				selectedRows={selectedRows}
				entity={{
					queryKey: "carrier",
					name: "Carrier",
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
